import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { IconBook, IconCreditCard, IconKey, IconSparkles } from '@tabler/icons-react';
import { Footer } from '../../../components/Footer';
import { Title } from '@mantine/core';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { a11yDark } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import {
  NODE_DATA_INGEST_TYPE,
  NODE_FETCH_DATABLOCK,
  NODE_PASSING_INGEST_DATA,
  NPM_CLIENT_INIT,
  PS_API_BASE_URL,
  PS_GET_DATABLOCK_ENDPOINT,
  PS_VARIABLE_FORMAT,
} from './Snippets';
import ScrollToTop from '../../../components/ScrollToTop';

function APIDocs() {
  useEffect(() => {
    document.title = 'Getting started | PromptSlate';
  }, []);

  return (
    <div className="flex flex-col w-full items-center h-fit">
      <ScrollToTop />
      <div className="w-full h-[45px] flex justify-center border-b">
        <div className="w-full max-w-5xl h-full flex items-center justify-between text-sm">
          <div>Introducting PromptSlate</div>
          <Link to="/signup">
            <div className="flex flex-row">
              Get started now <IconSparkles size="1rem" className="ml-2 mt-0.5" />
            </div>
          </Link>
        </div>
      </div>
      <div className="w-full max-w-5xl mt-2 mb-12">
        <div className="w-full max-w-5xl h-[70px] flex items-center justify-between">
          <div>
            <Link to="/">
              <img
                src="/logo_black.png"
                alt="PromptSlate logo"
                className="h-[30px] object-contain"
              />
            </Link>
          </div>
          <div className="flex flex-row">
            <div className="flex flex-row mr-5 p-2 rounded-lg hover:bg-slate-100 cursor-pointer duration-100 ease-in-out">
              <IconBook size="1.5rem" className="mr-1" /> Docs
            </div>
            <div className="flex flex-row mr-4 p-2 rounded-lg hover:bg-slate-100 cursor-pointer duration-100 ease-in-out">
              <IconCreditCard size="1.5rem" className="mr-1" /> Pricing
            </div>
            <Link to="/login">
              <div className="flex flex-row p-2 rounded-lg hover:bg-slate-100 cursor-pointer duration-100 ease-in-out">
                <IconKey size="1.5rem" className="mr-1" /> Login
              </div>
            </Link>
          </div>
        </div>
        <div className="mt-12 flex flex-col gap-4">
          <Title order={1}>Getting started</Title>
          <p>
            PromptSlate provide different ways to access the DataBlocks that you have created
            through the dashboard. This include language specific libraries and dirct APIs.
          </p>
          <p>
            In this getting started guide, we will go through both approaches and build an example
            application.
          </p>
          <Title order={2}>API Keys</Title>
          <p>
            You can generate API Keys from PromptSlate dashboard. The API can generated will target
            a specific environment in a specific project.{' '}
            <b>You will only be able to view the generated key once</b>. So please ensure that this
            is copied and stored securely in the secret manager of your choice. Anyone with the
            secret key will be able to request datablocks on your behalf so treat it just like you
            treat your passwords.
          </p>
          <Title order={2}>Libraries</Title>
          <Title order={3}>NPM</Title>
          <p>
            We have an npm library that you can make use to fetch DataBlocks from your NodeJS
            application. The library also provides features such as variable ingestion so that you
            don't have to write custom logic.
          </p>
          <p>Let's start by installing PromptSlate npm library</p>
          <SyntaxHighlighter language="npm" style={a11yDark}>
            npm i promptslate
          </SyntaxHighlighter>
          <p>
            Once you have the npm package installed, you can initialize a PromptSlate client in your
            application by using the syntax below
          </p>
          <SyntaxHighlighter language="javascript" style={a11yDark}>
            {NPM_CLIENT_INIT}
          </SyntaxHighlighter>
          <p>You can now use this client to perform different operations listed below.</p>
          <Title order={5}>Fetching datablock</Title>
          <p>
            To fetch a datablock which you created in a project, you should know its key. You can
            find them in the DataBlock preview
          </p>
          <p>
            Once you have the DataBlock key, you can make use of getDatablock function to fetch it
            as shown below
          </p>
          <SyntaxHighlighter language="javascript" style={a11yDark}>
            {NODE_FETCH_DATABLOCK}
          </SyntaxHighlighter>
          <p>
            Structure of the returned data can be found in the section below titled Data Structure.
          </p>
          <Title order={5}>Ingesting variables</Title>
          <p>
            Variables in DataBlocks can be ingested with custom data with the help of PromptSlate
            library. To ingest data to variables, an object can be created in the following
            structure by making use of type <b>SlateVariables</b>.
          </p>
          <SyntaxHighlighter language="javascript" style={a11yDark}>
            {NODE_DATA_INGEST_TYPE}
          </SyntaxHighlighter>
          <p>
            This object can then be passed as a second parameter while fetching the DataBlock using
            the getDatablock function
          </p>
          <SyntaxHighlighter language="javascript" style={a11yDark}>
            {NODE_PASSING_INGEST_DATA}
          </SyntaxHighlighter>
          <p>
            The returned result will follow the same data structure but all variables will be
            replaced with the data passed in SlateVariables object.
          </p>
          <Title order={2}>Using the API</Title>
          <p>
            If we don't officially provide a library for the language you are working with, you can
            always use our APIs directly to fetch DataBlocks. The response of the API will be in the
            same structure as the response from library.
          </p>
          <p>The base URL for PromptSlate API is</p>
          <SyntaxHighlighter language="javascript" style={a11yDark}>
            {PS_API_BASE_URL}
          </SyntaxHighlighter>
          <p>
            You can access the <b>GET</b> DataBlock API to fetch the datablock by hitting the
            following endpoint
          </p>
          <SyntaxHighlighter language="javascript" style={a11yDark}>
            {PS_API_BASE_URL + PS_GET_DATABLOCK_ENDPOINT}
          </SyntaxHighlighter>
          <p>
            For API authorization, you can use the API key generated from the dashboard and pass it
            as Authorization header value.
          </p>
          <p>
            To ingest data into variables, you will need to write logic within your application for
            replacing values. Since all variables follow the following format,
          </p>
          <SyntaxHighlighter language="javascript" style={a11yDark}>
            {PS_VARIABLE_FORMAT}
          </SyntaxHighlighter>
          <p>you should easily be able to replace them with some regex magic.</p>
          <Title order={2}>Data Structure</Title>
          <p>
            Both our libraries and APIs return DataBlocks in the same shape. The fields and the type
            of those fields are given below
          </p>
          <Title order={3}>Standard prompt</Title>
          <Title order={3}>Variable data</Title>
        </div>
        <Footer />
      </div>
    </div>
  );
}

export default APIDocs;
